import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useCreateOrganizationUserMutation } from "../../../../redux/api/org-user/orgUserAPI";
import ICreateOrganizationUserResponseDTO from "../../../../shared/oversight-core/dtos/response-dtos/create-organization-user-response-dto";
import { EOrgUserRole } from "../../../../shared/oversight-core/enums/org-user-role";
import { IAccessGrant } from "../../../../shared/oversight-core/interfaces/access-grant";
import { IAccessTarget } from "../../../../shared/oversight-core/interfaces/access-target";
import { IHttpError } from "../../../../shared/oversight-core/interfaces/http-errror";
import { ISeatCapView } from "../../../../shared/oversight-core/interfaces/seat-cap-view";
import AppModal from "../../../../shared/oversight-core/ui-elements/app-modal/app-modal";
import AppButton from "../../../../shared/oversight-core/ui-elements/buttons/app-button/app-button";
import AppInput from "../../../../shared/oversight-core/ui-elements/input/app-input";
import MaterialIcon from "../../../../shared/oversight-core/ui-elements/material-icon/material-icon";
import {
  getAllowedAccessLevels,
  isSeatPoolFull,
  orgUserRoleLabels,
  takesGuestSeat,
} from "../../../../shared/oversight-core/utils/get-org-user-info";
import { emailRegex } from "../../../../shared/oversight-core/utils/regex";
import { toRequestedGrant } from "../../users-helpers";
import AddAccessButton from "../add-access-button/add-access-button";
import AddAccessModal from "../add-access-modal/add-access-modal";
import OptionCard from "../option-card/option-card";
import AccessSummaryRow from "./access-summary-row/access-summary-row";

interface IFormValues {
  firstName: string;
  lastName: string;
  email: string;
  mobile: string;
}

interface IProps {
  show: boolean;
  tenantId: string;
  organizationName: string;
  seatCaps?: ISeatCapView;
  accessTargets: IAccessTarget[];
  isLoadingTargets: boolean;
  onClose: () => void;
  onCreated: (
    response: ICreateOrganizationUserResponseDTO,
    name: string,
  ) => void;
}

const defaultFormValues: IFormValues = {
  firstName: "",
  lastName: "",
  email: "",
  mobile: "",
};

// Accepts local (07X...) and international (+947X...) Sri Lankan mobiles, spaces allowed.
const sriLankanMobileRegex = /^(\+94|0)7\d{8}$/;

const labelClassName = "text-dark font-size-13 font-weight-500 mb-2";

const getSeatInfo = (grants: IAccessGrant[], seatCaps?: ISeatCapView) => {
  if (!seatCaps) return undefined;

  const isGuest = takesGuestSeat(grants);
  const kind = isGuest ? "guest" : "user";
  const max = isGuest ? seatCaps.maxGuestSeats : seatCaps.maxUserSeats;
  const occupied = isGuest
    ? seatCaps.occupiedGuestSeats
    : seatCaps.occupiedUserSeats;

  if (max === null || max === undefined) {
    return { label: `no ${kind} seat limit`, isFull: false };
  }

  const isGuestPoolFull = isSeatPoolFull(
    seatCaps.maxGuestSeats,
    seatCaps.occupiedGuestSeats,
  );
  // With user seats gone, a guest can still be added, so say how rather than "no seats".
  const getFullMessage = () => {
    if (isGuest) return "No guest seats left.";
    if (isGuestPoolFull)
      return "No seats left. Remove a user or ask for more seats.";
    return "No user seats left. You can still add a guest: a user whose grants are all View redacted.";
  };

  return {
    label: `${Math.max(max - occupied, 0)} of ${max} ${kind} seats left`,
    isFull: isSeatPoolFull(max, occupied),
    fullMessage: getFullMessage(),
  };
};

const AddUserModal = (props: IProps) => {
  const {
    show,
    tenantId,
    organizationName,
    seatCaps,
    accessTargets,
    isLoadingTargets,
    onClose,
    onCreated,
  } = props;
  const [role, setRole] = useState<EOrgUserRole>();
  const [grants, setGrants] = useState<IAccessGrant[]>([]);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [showAddAccess, setShowAddAccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [createUser, { isLoading: isCreating }] =
    useCreateOrganizationUserMutation();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    getValues,
    trigger,
    formState: { errors },
  } = useForm<IFormValues>({ defaultValues: { ...defaultFormValues } });

  useEffect(() => {
    if (show) {
      reset({ ...defaultFormValues });
      setRole(undefined);
      setGrants([]);
      setSubmitAttempted(false);
      setShowAddAccess(false);
      setErrorMessage(undefined);
    }
  }, [show, reset]);

  const fullName = `${watch("firstName")} ${watch("lastName")}`.trim();
  const allowedLevels = getAllowedAccessLevels(role);
  const hasLevelNotAllowed = grants.some(
    (g) => !allowedLevels.includes(g.level),
  );
  const seatInfo = getSeatInfo(grants, seatCaps);

  const onSubmit = (values: IFormValues) => {
    if (!role || hasLevelNotAllowed) return;

    const name = `${values.firstName.trim()} ${values.lastName.trim()}`;
    setErrorMessage(undefined);
    createUser({
      tenantId,
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      email: values.email.trim() || undefined,
      mobileNumber: values.mobile.replace(/\s/g, "") || undefined,
      role,
      grants: grants.map(toRequestedGrant),
    })
      .unwrap()
      .then((response) => onCreated(response, name))
      // On a 412 nothing is created, so the dialog stays open with the reason.
      .catch((error: IHttpError) =>
        setErrorMessage(error?.message || "Sorry, an error occurred."),
      );
  };

  const getFooterMessage = () => {
    if (errorMessage) return errorMessage;
    if (seatInfo?.isFull) return seatInfo.fullMessage;
    if (submitAttempted && hasLevelNotAllowed)
      return "A Monitoring guest can only be created with View redacted grants.";
    return undefined;
  };
  const footerMessage = getFooterMessage();

  // An email or a mobile number is needed to invite the user. Mobile is the optional one, so the
  // requirement is reported under Email only, and a mobile number satisfies it.
  const requireContact = () =>
    !!getValues("email").trim() ||
    !!getValues("mobile").trim() ||
    "Email is required unless you add a mobile number";

  return (
    <>
      {/* Add access replaces this modal while it is open; Back returns here with the form intact. */}
      <AppModal show={show && !showAddAccess} size="modal-lg">
        <>
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <div className="text-dark font-size-24 font-weight-600">
                Add user
              </div>
              <div className="d-flex align-items-center gap-1 text-light font-size-13 mt-1">
                <MaterialIcon icon="person_outline" size={16} />
                {[`To ${organizationName}`, seatInfo?.label]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
            </div>
            <MaterialIcon
              icon="close"
              className="cursor-pointer"
              onClick={onClose}
            />
          </div>

          <Row className="g-3 mt-2">
            <Col className="col-12 col-sm-6">
              <AppInput
                label="First name"
                placeholder="First name"
                name="firstName"
                isRequired={false}
                labelFontSize={labelClassName}
                register={register("firstName", {
                  validate: (v) => !!v.trim() || "First name is required",
                })}
                errors={errors}
              />
            </Col>
            <Col className="col-12 col-sm-6">
              <AppInput
                label="Last name"
                placeholder="Last name"
                name="lastName"
                isRequired={false}
                labelFontSize={labelClassName}
                register={register("lastName", {
                  validate: (v) => !!v.trim() || "Last name is required",
                })}
                errors={errors}
              />
            </Col>
            <Col className="col-12 col-sm-6">
              <AppInput
                label="Email"
                placeholder="name@example.com"
                name="email"
                type="email"
                isRequired={false}
                labelFontSize={labelClassName}
                register={register("email", {
                  validate: {
                    contact: requireContact,
                    format: (v) =>
                      !v.trim() ||
                      emailRegex.test(v.trim()) ||
                      "Enter a valid email address",
                  },
                  onChange: () => submitAttempted && trigger("mobile"),
                })}
                errors={errors}
              />
            </Col>
            <Col className="col-12 col-sm-6">
              <AppInput
                label="Mobile (optional)"
                placeholder="+94 7X XXX XXXX"
                name="mobile"
                isRequired={false}
                labelFontSize={labelClassName}
                register={register("mobile", {
                  validate: {
                    format: (v) =>
                      !v.trim() ||
                      sriLankanMobileRegex.test(v.replace(/\s/g, "")) ||
                      "Enter a valid mobile number",
                  },
                  onChange: () => submitAttempted && trigger("email"),
                })}
                errors={errors}
              />
            </Col>
          </Row>

          <div className={`${labelClassName} mt-4`}>Role</div>
          <Row role="radiogroup" className="g-3">
            {Object.values(EOrgUserRole).map((r) => (
              <Col key={r} className="col-12 col-sm-6">
                <OptionCard
                  title={orgUserRoleLabels[r]}
                  selected={role === r}
                  onClick={() => setRole(r)}
                />
              </Col>
            ))}
          </Row>
          {submitAttempted && !role && (
            <span className="error">Choose a role</span>
          )}

          <div className="d-flex justify-content-between align-items-center mt-4 mb-2">
            <span className="text-dark font-size-13 font-weight-500">
              Access
            </span>
            <AddAccessButton onClick={() => setShowAddAccess(true)} />
          </div>
          <div className="d-flex flex-column gap-2">
            {grants.map((grant) => (
              <AccessSummaryRow
                key={grant.identity}
                grant={grant}
                onRemove={() =>
                  setGrants((ps) =>
                    ps.filter((g) => g.identity !== grant.identity),
                  )
                }
              />
            ))}
          </div>
          <p className="text-light font-size-12 mt-2 mb-0">
            A user with no access sees nothing after signing in. Add at least
            one grant.
          </p>

          <Row className="align-items-center g-3 mt-4">
            <Col className="col-auto">
              <AppButton
                text="Create user"
                size="medium"
                isLoading={isCreating}
                disabled={!!seatInfo?.isFull}
                onClick={() => {
                  setSubmitAttempted(true);
                  handleSubmit(onSubmit)();
                }}
              />
            </Col>
            <Col className="col-auto">
              <AppButton
                text="Cancel"
                variant="transparentWithBorder"
                size="medium"
                onClick={onClose}
              />
            </Col>
            {footerMessage && (
              <Col className="text-danger font-size-12">{footerMessage}</Col>
            )}
          </Row>
        </>
      </AppModal>
      <AddAccessModal
        show={show && showAddAccess}
        userName={fullName || "the new user"}
        existingGrants={grants}
        accessTargets={accessTargets}
        isLoadingTargets={isLoadingTargets}
        allowedLevels={allowedLevels}
        onBack={() => setShowAddAccess(false)}
        onSave={(grant) => {
          setGrants((ps) => [...ps, grant]);
          setShowAddAccess(false);
        }}
      />
    </>
  );
};

export default AddUserModal;
